import { useState, useEffect, ReactNode, lazy } from "react";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { mitWebseiteWeiche } from "@/components/webseite/WebseiteWeiche";
import { Button } from "./ui/button";
import { NavLink } from "./NavLink";
import logoImage from "@/assets/musikonzept-logo.png";

import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { 
  ChevronDown, 
  ChevronRight,
  Book, 
  Menu, 
  X,
  Laptop,
  Users,
  Building2,
  Info,
  HelpCircle,
  Mail,
  MessageSquare,
  ShoppingBag,
  Guitar,
  ExternalLink,
  GraduationCap,
  Music2,
  Sparkles,
  LogIn,
  LayoutDashboard,
  Compass
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MegaMenu } from "@/components/MegaMenu";
import { AusbildungenMegaMenu } from "@/components/AusbildungenMegaMenu";
import { KurseMegaMenu } from "@/components/KurseMegaMenu";
import { MehrMegaMenu } from "@/components/MehrMegaMenu";
import { InfoLeiste, InfoLeisteAbstand, InfoPopupSteuerung } from "@/components/termine/InfoLeiste";

// Mobile Accordion Component
const MobileAccordion = ({ 
  title, 
  icon, 
  children 
}: { 
  title: string; 
  icon: ReactNode; 
  children: ReactNode 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mx-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/10 rounded-lg font-medium transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </div>
        <ChevronRight 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-2 pb-2">
          {children}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const istApp = useIsNativeApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ausbildungenOpen, setAusbildungenOpen] = useState(false);
  const [kurseOpen, setKurseOpen] = useState(false);
  const [mehrOpen, setMehrOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Check initial auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const closeAllMenus = () => {
    setAusbildungenOpen(false);
    setKurseOpen(false);
    setMehrOpen(false);
  };

  const openMenu = (menuName: 'kurse' | 'ausbildungen' | 'mehr') => {
    closeAllMenus();
    switch(menuName) {
      case 'kurse':
        setKurseOpen(true);
        break;
      case 'ausbildungen':
        setAusbildungenOpen(true);
        break;
      case 'mehr':
        setMehrOpen(true);
        break;
    }
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
      {/* Hinweisleiste über dem Menü (Daniel 22.10.2026); ohne Schalter zeichnet sie nichts. */}
      <InfoLeiste />
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <NavLink to="/" className="flex items-center">
            <img 
              src={logoImage} 
              alt="MusiKonzept Logo" 
              className="h-12 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
          </NavLink>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center gap-1 flex-1 justify-center font-display font-bold"
          >
            {/* Start */}
            <NavLink
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              START
            </NavLink>

            {/* KURSE Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => openMenu('kurse')}
              onMouseLeave={closeAllMenus}
            >
              <button
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                onClick={() => kurseOpen ? closeAllMenus() : openMenu('kurse')}
              >
                KURSE
                <ChevronDown className="w-4 h-4" />
              </button>
              <MegaMenu isOpen={kurseOpen} onClose={closeAllMenus}>
                <KurseMegaMenu onClose={closeAllMenus} />
              </MegaMenu>
            </div>

            <div 
              className="relative"
              onMouseEnter={() => openMenu('ausbildungen')}
              onMouseLeave={closeAllMenus}
            >
              <button
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                onClick={() => ausbildungenOpen ? closeAllMenus() : openMenu('ausbildungen')}
              >
                AUSBILDUNGEN
                <ChevronDown className="w-4 h-4" />
              </button>
              <MegaMenu isOpen={ausbildungenOpen} onClose={closeAllMenus}>
                <AusbildungenMegaMenu onClose={closeAllMenus} />
              </MegaMenu>
            </div>

            {/* Termine (kein Dropdown) */}
            <NavLink
              to="/termine"
              className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              onMouseEnter={closeAllMenus}
            >
              TERMINE
            </NavLink>

            {/* Mitgliedschaft (kein Dropdown); in der App nicht (Sitzung A1) */}
            {!istApp && (
            <NavLink
              to="/preise"
              className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              onMouseEnter={closeAllMenus}
            >
              MITGLIEDSCHAFT
            </NavLink>
            )}

            {/* Musik-Kita (kein Dropdown) */}
            <NavLink
              to="/musik-kita"
              className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
              onMouseEnter={closeAllMenus}
            >
              MUSIK-KITA
            </NavLink>

            {/* MEHR Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => openMenu('mehr')}
              onMouseLeave={closeAllMenus}
            >
              <button
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                onClick={() => mehrOpen ? closeAllMenus() : openMenu('mehr')}
              >
                MEHR
                <ChevronDown className="w-4 h-4" />
              </button>
              <MegaMenu isOpen={mehrOpen} onClose={closeAllMenus} align="right">
                <MehrMegaMenu onClose={closeAllMenus} />
              </MegaMenu>
            </div>

          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <NavLink to="/mein-bereich" onClick={closeAllMenus}>
                <Button 
                  className="bg-gradient-to-r from-primary to-accent text-white font-display font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Mein Bereich
                </Button>
              </NavLink>
            ) : (
              <>
                <NavLink to="/registrieren" onClick={closeAllMenus}>
                  <Button 
                    className="bg-gradient-to-r from-primary to-accent text-white font-display font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Jetzt entdecken
                  </Button>
                </NavLink>
                <NavLink to="/login" onClick={closeAllMenus}>
                  <Button 
                    variant="outline"
                    className="font-display font-semibold border-foreground/20 hover:bg-foreground/5"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                </NavLink>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 hover:bg-accent/10 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? (
              <X className="w-10 h-10 stroke-[2.5]" />
            ) : (
              <div className="flex flex-col justify-center items-center gap-[6px] w-10 h-10">
                <span className="block w-8 h-[3px] bg-foreground rounded-full" />
                <span className="block w-8 h-[3px] bg-foreground rounded-full" />
                <span className="block w-8 h-[3px] bg-foreground rounded-full" />
              </div>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 space-y-2 border-t border-border bg-card/95 backdrop-blur-md shadow-lg font-display font-semibold max-h-[calc(100vh-80px)] overflow-y-auto">
            <NavLink 
              to="/" 
              className="block px-4 py-3 hover:bg-accent/10 rounded-lg mx-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              START
            </NavLink>

            {/* Mobile KURSE Accordion */}
            <MobileAccordion
              title="KURSE"
              icon={<Book className="w-4 h-4" />}
            >
              <div className="space-y-1 pt-2">
                {/* Dieselben Ziele wie im Menü am Computer (KurseMegaMenu); die alten Thinkific-Adressen
                    /collections?c=… verloren den Filter (Sitzung „Aufräumen“, 14.10.2026). */}
                <NavLink
                  to="/kurse?kategorie=kreativpakete"
                  className="flex items-center justify-between px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Kreativpakete</span>
                </NavLink>
                <NavLink
                  to="/kurse?kategorie=online-fortbildungen"
                  className="flex items-center justify-between px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Online-Fortbildungen</span>
                </NavLink>
                <NavLink
                  to="/kurse?kategorie=webinar-aufzeichnungen"
                  className="flex items-center justify-between px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Webinar-Aufzeichnungen</span>
                </NavLink>
                <NavLink
                  to="/kurse?kategorie=gitarre-ukulele"
                  className="flex items-center justify-between px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Gitarre & Ukulele</span>
                </NavLink>
                <NavLink
                  to="/kurse?kategorie=liederwerkstatt"
                  className="flex items-center justify-between px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Liederwerkstatt</span>
                </NavLink>
                <NavLink
                  to="/kurse"
                  className="flex items-center justify-between px-4 py-2 hover:bg-primary/10 rounded-lg text-sm font-semibold text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Alle Kurse (Online-Akademie)</span>
                </NavLink>
                <NavLink 
                  to="/themenreisen" 
                  className="flex items-center gap-2 px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Compass className="w-4 h-4 text-primary" />
                  <span>Themenreisen</span>
                </NavLink>
              </div>
            </MobileAccordion>


            {/* Mobile AUSBILDUNGEN Accordion */}
            <MobileAccordion
              title="AUSBILDUNGEN"
              icon={<GraduationCap className="w-4 h-4" />}
            >
              <div className="space-y-3 pt-2">
                <div className="bg-accent/5 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Music2 className="w-4 h-4 text-accent" />
                    <span className="font-medium text-sm">Fachkraft für Musik im Elementarbereich</span>
                  </div>
                  <div className="space-y-1 ml-6">
                    <NavLink 
                      to="/ausbildung/fachkraft-musik" 
                      className="block px-3 py-2 hover:bg-background rounded-lg text-sm"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Übersicht
                    </NavLink>
                    <NavLink 
                      to="/ausbildung/fachkraft-musik-und-sprache" 
                      className="block px-3 py-2 hover:bg-background rounded-lg text-sm"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sprachförderung
                    </NavLink>
                  </div>
                </div>
                <NavLink 
                  to="/ausbildung/fachkraft-gitarre" 
                  className="flex items-center gap-2 px-4 py-2 hover:bg-accent/10 rounded-lg text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Guitar className="w-4 h-4 text-accent" />
                  <span>Der große Kita-Gitarrenkurs</span>
                </NavLink>
              </div>
            </MobileAccordion>

            <NavLink 
              to="/termine" 
              className="block px-4 py-3 hover:bg-accent/10 rounded-lg mx-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              TERMINE
            </NavLink>

            {!istApp && (
            <NavLink 
              to="/preise" 
              className="block px-4 py-3 hover:bg-accent/10 rounded-lg mx-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              MITGLIEDSCHAFT
            </NavLink>
            )}

            <NavLink 
              to="/musik-kita" 
              className="block px-4 py-3 hover:bg-accent/10 rounded-lg mx-2 font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              MUSIK-KITA
            </NavLink>

            {/* Mobile MEHR Accordion */}

            <MobileAccordion
              title="MEHR"
              icon={<Info className="w-4 h-4" />}
            >
              <div className="space-y-1 pt-2">
                {/* Für wen? - NEU */}
                <div className="px-2 py-1">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Für wen?</div>
                  <NavLink 
                    to="/paedagogische-fachkraefte" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Users className="w-4 h-4 text-primary" />
                    <span>Pädagogische Fachkräfte</span>
                  </NavLink>
                  <NavLink 
                    to="/musik-kita" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building2 className="w-4 h-4 text-primary" />
                    <span>Kitas & Einrichtungen</span>
                  </NavLink>
                  <div className="ml-6 space-y-0.5">
                    <NavLink 
                      to="/musik-kita-verzeichnis" 
                      className="flex items-center gap-2 px-2 py-1 hover:bg-accent/10 rounded-lg text-xs"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Book className="w-3 h-3 text-accent" />
                      <span>Musik-Kita-Verzeichnis</span>
                    </NavLink>
                  </div>
                </div>

                {/* Angebote vor Ort */}
                <div className="px-2 py-1">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Angebote vor Ort</div>
                  <NavLink 
                    to="/inhouse-schulungen" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Users className="w-4 h-4 text-primary" />
                    <span>Fortbildung vor Ort</span>
                  </NavLink>
                  <NavLink 
                    to="/musikonzept-vor-ort" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Building2 className="w-4 h-4 text-primary" />
                    <span>MusiKonzept vor Ort</span>
                  </NavLink>
                </div>

                {/* Über uns */}
                <div className="px-2 py-1">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Über uns</div>
                  <NavLink 
                    to="/ueber-uns" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Info className="w-4 h-4 text-primary" />
                    <span>Über MusiKonzept</span>
                  </NavLink>
                  <NavLink 
                    to="/community" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Users className="w-4 h-4 text-primary" />
                    <span>Community</span>
                  </NavLink>
                </div>

                {/* Support */}
                <div className="px-2 py-1">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Support</div>
                  <NavLink 
                    to="/kontakt" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Mail className="w-4 h-4 text-primary" />
                    <span>Kontakt</span>
                  </NavLink>
                  <NavLink 
                    to="/newsletter" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <MessageSquare className="w-4 h-4 text-primary" />
                    <span>Newsletter</span>
                  </NavLink>
                  <NavLink 
                    to="/hilfe" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HelpCircle className="w-4 h-4 text-primary" />
                    <span>Hilfe & FAQ</span>
                  </NavLink>
                </div>

                {/* Shop */}
                <div className="px-2 py-1">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Shop</div>
                  <a 
                    href="https://shop.musikonzept.de" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/10 rounded-lg text-sm"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ShoppingBag className="w-4 h-4 text-primary" />
                    <span>Zum Shop</span>
                    <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto" />
                  </a>
                </div>
              </div>
            </MobileAccordion>

            <div className="px-4 pt-4 pb-2 border-t border-border/50 mt-2 flex flex-col gap-3">
              {user ? (
                <NavLink to="/mein-bereich" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button 
                    className="w-full bg-gradient-to-r from-primary to-accent text-white font-semibold h-12"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Mein Bereich
                  </Button>
                </NavLink>
              ) : (
                <>
                  <NavLink to="/registrieren" onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button 
                      className="w-full bg-gradient-to-r from-primary to-accent text-white font-semibold h-12 text-base"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Jetzt entdecken
                    </Button>
                  </NavLink>
                  <NavLink to="/login" onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button 
                      variant="outline"
                      className="w-full font-semibold border-foreground/20 h-12 text-base"
                    >
                      <LogIn className="w-5 h-5 mr-2" />
                      Login
                    </Button>
                  </NavLink>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
    {/* Platz für die Hinweisleiste im Kopf (Daniel 22.10.2026); ohne Schalter zeichnet sie nichts. */}
    <InfoLeisteAbstand />
    <InfoPopupSteuerung />
    </>
  );
};

// Weiche „Webseite neu“ (Sitzung 9): ohne Weiche genau dieser Kopf, mit Weiche der neue aus src/components/webseite/.
const HeaderMitWeiche = mitWebseiteWeiche(Header, lazy(() => import("@/components/webseite/Kopf")));
export default HeaderMitWeiche;
